// Modal de "Regerar" da cruzada (catálogo de Passatempos).
//
// O backend ENCAIXA as palavras e pode não conseguir posicionar todas: a resposta traz
// `naoColocadas`. Se o modal simplesmente fechasse no sucesso, o admin publicaria um
// jogo incompleto sem saber — por isso, quando sobra palavra de fora, o modal fica
// aberto mostrando quais foram.
import { useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Typography, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

import { regenerateCrossword, type PassatempoAdmin } from '../../services/admin';

interface RegenerateItem {
  palavra: string;
  dica?: string;
}

interface RegenerateFormValues {
  titulo?: string;
  itens: RegenerateItem[];
}

interface Result {
  colocadas: string[];
  naoColocadas: string[];
}

interface Props {
  /** Passatempo a regerar; `null` mantém o modal fechado. */
  target: PassatempoAdmin | null;
  onClose: () => void;
  /** Chamado depois de uma regeração bem-sucedida, para a lista recarregar. */
  onDone: () => void;
}

export const RegenerateCrosswordModal: React.FC<Props> = ({ target, onClose, onDone }) => {
  const [form] = Form.useForm<RegenerateFormValues>();
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const close = () => {
    setResult(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!target) return;
    let values: RegenerateFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // o form já exibiu os erros
    }

    const itens = values.itens
      .map((i) => ({ palavra: i.palavra.trim(), dica: i.dica?.trim() || undefined }))
      .filter((i) => i.palavra.length > 0);

    if (itens.length === 0) {
      message.error('Informe ao menos uma palavra.');
      return;
    }

    const titulo = values.titulo?.trim();

    setSaving(true);
    try {
      const res = await regenerateCrossword(target.id, {
        ...(titulo ? { titulo } : {}),
        itens,
      });
      onDone();

      if (res.naoColocadas.length > 0) {
        // Sucesso parcial: mantém o modal aberto para o admin ver o que ficou de fora.
        setResult(res);
      } else {
        message.success(`Cruzada regerada com ${res.colocadas.length} palavra(s).`);
        close();
      }
    } catch (err: any) {
      message.error(err?.response?.data?.msg ?? 'Falha ao regerar a cruzada.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={target ? `Regerar cruzada #${target.id}` : 'Regerar cruzada'}
      visible={target !== null}
      onCancel={close}
      destroyOnClose
      width={640}
      footer={
        result ? (
          <Button type="primary" onClick={close}>
            Fechar
          </Button>
        ) : (
          <Space>
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" danger loading={saving} onClick={handleSubmit}>
              Regerar
            </Button>
          </Space>
        )
      }
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="warning"
            showIcon
            message="Nem todas as palavras couberam na grade"
            description={
              <>
                <Typography.Paragraph style={{ marginBottom: 8 }}>
                  A cruzada foi regerada com <strong>{result.colocadas.length}</strong> palavra(s).
                  As abaixo <strong>não cruzaram</strong> e ficaram de fora do jogo:
                </Typography.Paragraph>
                <Typography.Text code>{result.naoColocadas.join(', ')}</Typography.Text>
              </>
            }
          />
        </Space>
      ) : (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="A grade atual será substituída pelas palavras abaixo."
          />
          <Form form={form} layout="vertical" preserve={false} initialValues={{ itens: [{}] }}>
            <Form.Item
              name="titulo"
              label="Título"
              extra="Deixe vazio para manter o título atual."
            >
              <Input placeholder={target?.titulo ?? 'Palavras cruzadas'} />
            </Form.Item>

            <Form.List name="itens">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'palavra']}
                        rules={[{ required: true, message: 'Informe a palavra.' }]}
                      >
                        <Input placeholder="Palavra" style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, 'dica']}>
                        <Input placeholder="Dica (opcional)" style={{ width: 300 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Adicionar palavra
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default RegenerateCrosswordModal;
